import firebase from 'firebase/compat/app';
import moment from 'moment';
import { getFirebaseAuth, ensureFirebaseInitialized } from './firebase';
import { retryOnError } from '../tools/retryUtils';

const getDb = () => {
  ensureFirebaseInitialized();
  return firebase.firestore();
};


export function createNewId() {
  const document = getDb().collection('/idontexist').doc();
  return document.id;
}

export class MismatchedPathsError extends Error {
  constructor(ids, collections) {
    super(`Paths in a subcollection query must have the same length: ids = ${ids}, collections = ${collections}`);
  }
}

export function getParentRef(pathIds, pathCollections) {
  const ids = pathIds.split('.');
  const collections = pathCollections.split('.');

  if (ids.length !== collections.length) {
    throw new MismatchedPathsError(pathIds, pathCollections);
  }

  let query = getDb();
  collections.forEach((collection, index) => {
    query = query.collection(collection).doc(ids[index]);
  });

  return query;
}

export function firestoreDocumentsBySubCollectionsQuery(
  pathIds,
  pathCollections,
  subcollection,
  queryCriteria,
  orderBy,
) {
  const ref = getParentRef(pathIds, pathCollections);
  let query = ref.collection(subcollection);
  queryCriteria.forEach((criterion) => {
    query = query.where(criterion.key, criterion.operator, criterion.value);
  });

  if (orderBy) {
    query = query.orderBy(orderBy.field, orderBy.order);
  }
  return query;
}

export function getFieldValue() {
  return firebase.firestore.FieldValue;
}

export function getDocumentIdField() {
  return firebase.firestore.FieldPath.documentId();
}

export function firestoreDocumentByPath(path) {
  return getDb().doc(path);
}

export function firestoreDocumentById(collection, id) {
  return getDb()
    .collection(collection)
    .doc(id);
}

export function firestoreCollection(collection) {
  return getDb()
    .collection(collection);
}

export function firestoreCollectionGroup(collection) {
  return getDb()
    .collectionGroup(collection);
}

export function configureQueryByCollectionPath(collectionPath, criteria, orderBy, limit) {
  const query = firestoreCollection(collectionPath);
  return configureQuery(query, criteria, orderBy, limit);
}

export function configureQuery(query, criteria = [], orderBy, limit) {
  let configuredQuery = query;
  criteria.forEach(criterion => {
    configuredQuery = configuredQuery.where(criterion.key, criterion.operator, criterion.value);
  });

  if (orderBy) {
    configuredQuery = configuredQuery.orderBy(orderBy.field, orderBy.order);
  }

  if (limit) {
    configuredQuery = configuredQuery.limit(limit);
  }
  return configuredQuery;
}

export function firestoreDocumentsByQuery(collection, criteria = [], orderBy, limit) {
  return configureQuery(firestoreCollection(collection), criteria, orderBy, limit);
}

export function onSnapshot(query, callback, options = {}) {
  const {
    retry = false,
    onError = (error) => {
      console.error('onSnapshot failed for query with path', query.path, error);
    },
  } = options;

  const onSnapshots = [];
  let onSnapshotIsCanceled = false;
  const rootOnSnapshot = query
    .onSnapshot({
      next: (snapshot) => {
        callback(snapshot);
      },
      onNext: (snapshot) => {
        callback(snapshot);
      },
      error: async (error) => {
        if (!retry) {
          onError(error);
          return;
        }

        try {
          await retryOnError(async () => {
            if (onSnapshotIsCanceled) return;

            await query.get();

            const newOnSnapshot = onSnapshot(query, callback, options);
            onSnapshots.push(newOnSnapshot);
          });
        } catch (retryError) {
          onError(retryError);
        }
      },
    });
  onSnapshots.push(rootOnSnapshot);

  const cancelOnSnapshot = () => {
    onSnapshotIsCanceled = true;
    onSnapshots.forEach(unsubscribe => {
      unsubscribe();
    });
  };
  return cancelOnSnapshot;
}

export async function update(query, data, options = {}) {
  return query.update(prepareDocument(data, options));
}

export async function set(query, data, options = {}) {
  return query.set(prepareDocument(data, options));
}

export async function removeDocumentInCollection(collection, id) {
  const doc = firestoreDocumentById(collection, id);
  return doc.delete();
}

export async function setMerge(query, data, options = {}) {
  return query.set(prepareDocument(data, options), { merge: true });
}

const prepareDocument = (data, options = {}) => {
  if (!(data instanceof Object)) {
    console.log(`${data} is not an object`);
    throw Error('Document must be an object');
  }
  if (options.undefinedSafe !== true) {
    removeUndefined(data);
  }
  return applyAuditInfo(data);
};

export const removeUndefined = (data) => {
  Object.keys(data).forEach((key) => {
    if (data[key] === undefined) {
      delete data[key];
    } else if (data[key] instanceof Object) {
      removeUndefined(data[key]);
    }
  });
};

const applyAuditInfo = (data) => {
  const userId = getFirebaseAuth().currentUser.uid;
  data.auditInfo = {
    modifiedBy: userId,
    modifiedOn: moment().valueOf(),
  };

  return data;
};

export async function executeQueryRaw(query) {
  return query.get();
}

export async function executeQuery(query) {
  const result = await query.get();

  if (result.docs) {
    return formatDocuments(result.docs);
  }

  return formatDocument(result);
}

function formatDocument(doc) {
  return { id: doc.id, ...convertDocument(doc.data()) };
}

function formatDocuments(documents) {
  const formattedDocuments = [];
  documents.forEach(doc => {
    const formattedDocument = formatDocument(doc);
    formattedDocuments.push(formattedDocument);
  });
  return formattedDocuments;
}

function convertDocument(document) {
  const convertedDocument = {};
  Object.keys(document).forEach(key => {
    convertedDocument[key] = convertValue(document[key]);
  });
  return convertedDocument;
}

function convertValue(value) {
  let result;
  if (value instanceof firebase.firestore.Timestamp) {
    result = value.toDate();
  } else if (value instanceof Array) {
    result = value.map(item => convertValue(item));
  } else if (value instanceof Object) {
    result = convertDocument(value);
  } else {
    result = value;
  }
  return result;
}

export async function* queryIterator(queryFunction, batchSize) {
  let lastSnapshot;

  const next = async () => {
    let query = queryFunction();
    query = query.limit(batchSize);


    if (lastSnapshot) {
      query = query.startAfter(lastSnapshot);
    }

    const response = await query.get();
    return response.docs;
  };

  while (true) {
    const docs = await next(); // eslint-disable-line
    if (docs.length === 0) {
      yield formatDocuments(docs);
      return;
    }

    lastSnapshot = docs[docs.length - 1];
    yield formatDocuments(docs);
  }
}
